import { NodeProps } from 'reactflow';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { pluralize, unmaskNumber } from '@givebutter/util';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { faBolt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CampaignBadges } from 'assets/js/pages/engage/workflows/components/CampaignBadges';
import {
    CampaignSelect,
    defaultCampaignSelectValue,
} from 'assets/js/pages/engage/workflows/components/CampaignSelect';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import * as NodeBadges from 'assets/js/pages/engage/workflows/components/NodeBadges';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import {
    LastGiftMadeTriggerFormValues,
    LastGiftMadeTriggerNodeData,
    TimeAgoCustomUnit,
    TimeAgoIntervalValue,
} from 'assets/js/pages/engage/workflows/triggers/types';
import { enabledTriggers } from 'assets/js/pages/engage/workflows/utils/starterItems';
import * as Form from 'components/form';
import { WorkflowTriggerTypeEnum } from 'constants/workflows';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { lowerCase } from 'lodash';
import { DateTime } from 'luxon';

interface LastGiftMadeTriggerProps extends NodeProps<LastGiftMadeTriggerNodeData> {}

const LastGiftMadeTrigger = ({ id, selected, data }: LastGiftMadeTriggerProps) => {
    const { timezone } = useCurrentAccount();
    const { formData } = data;
    const methods = Form.useGivebutterForm<LastGiftMadeTriggerFormValues>({
        mode: 'all',
        defaultValues: {
            campaigns: formData?.values.campaigns || defaultCampaignSelectValue,
            interval: formData?.values.interval || '1 year',
            customAmount: formData?.values.customAmount || '',
            customUnit: formData?.values.customUnit || 'year',
        },
    });
    const {
        control,
        formState: { errors },
        watch,
        path,
        getValues,
        trigger,
    } = methods;
    const isError = Object.keys(errors).length > 0;
    const { interval, customAmount } = watch();

    useSyncFormToNode<LastGiftMadeTriggerFormValues>({ nodeId: id, control });
    useDebouncedAutosave<LastGiftMadeTriggerFormValues>({ methods });
    useTriggerValidationOnDeselection<LastGiftMadeTriggerFormValues>({ trigger, selected });

    const selectedTrigger = enabledTriggers.find(
        t => t.slug === WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION,
    );
    if (!selectedTrigger) return;

    return (
        <Form.Provider {...methods}>
            <StepFormContainer>
                <CanvasNode.Root
                    className="max-w-96"
                    isSelected={selected}
                    isError={isError}
                    id={id}
                >
                    <CanvasNode.Container>
                        <CanvasNode.Header>
                            <CanvasNode.Icon icon={faBolt} className="text-yellow-300" />
                            <div className="flex flex-col items-start">
                                <CanvasNode.Title>{selectedTrigger.name}</CanvasNode.Title>
                                {selected ? (
                                    <CanvasNode.Description>
                                        Configure your workflow trigger. Your workflow will initiate
                                        at 10 AM (
                                        {DateTime.now().setZone(timezone).toFormat('ZZZZ')}).
                                    </CanvasNode.Description>
                                ) : null}
                            </div>
                        </CanvasNode.Header>
                        <CanvasNode.Body>
                            <CanvasNode.BodySelected>
                                <CampaignSelect />

                                <Form.Container>
                                    <Form.Label name={path('interval')}>Last gift made</Form.Label>
                                    <div className="flex flex-col gap-2">
                                        <Form.SingleSelect control={control} name="interval">
                                            <SingleSelect.Trigger aria-label="Interval" />
                                            <SingleSelect.Content>
                                                <SingleSelect.Item value="3 months">
                                                    <SingleSelect.ItemText>
                                                        3 months ago
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="6 months">
                                                    <SingleSelect.ItemText>
                                                        6 months ago
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="1 year">
                                                    <SingleSelect.ItemText>
                                                        1 year ago
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="2 years">
                                                    <SingleSelect.ItemText>
                                                        2 years ago
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="custom">
                                                    <SingleSelect.ItemText>
                                                        Custom
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>
                                        {interval === 'custom' ? (
                                            <div className="grid grid-cols-2 gap-2">
                                                <Form.TextInputRoot
                                                    control={control}
                                                    name="customAmount"
                                                    inputRootProps={{
                                                        mask: 'whole-number',
                                                    }}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faClock}
                                                        className="text-gray-400"
                                                    />
                                                    <Form.TextInputForm />
                                                </Form.TextInputRoot>
                                                <Form.SingleSelect
                                                    control={control}
                                                    name="customUnit"
                                                >
                                                    <SingleSelect.Trigger aria-label="Unit" />
                                                    <SingleSelect.Content>
                                                        <SingleSelect.Item value="week">
                                                            <SingleSelect.ItemText>
                                                                {transformSelectedUnit(
                                                                    'week',
                                                                    customAmount,
                                                                )}
                                                            </SingleSelect.ItemText>
                                                        </SingleSelect.Item>
                                                        <SingleSelect.Item value="month">
                                                            <SingleSelect.ItemText>
                                                                {transformSelectedUnit(
                                                                    'month',
                                                                    customAmount,
                                                                )}
                                                            </SingleSelect.ItemText>
                                                        </SingleSelect.Item>
                                                        <SingleSelect.Item value="quarter">
                                                            <SingleSelect.ItemText>
                                                                {transformSelectedUnit(
                                                                    'quarter',
                                                                    customAmount,
                                                                )}
                                                            </SingleSelect.ItemText>
                                                        </SingleSelect.Item>
                                                        <SingleSelect.Item value="year">
                                                            <SingleSelect.ItemText>
                                                                {transformSelectedUnit(
                                                                    'year',
                                                                    customAmount,
                                                                )}
                                                            </SingleSelect.ItemText>
                                                        </SingleSelect.Item>
                                                    </SingleSelect.Content>
                                                </Form.SingleSelect>
                                            </div>
                                        ) : null}
                                    </div>
                                </Form.Container>
                            </CanvasNode.BodySelected>
                            <CanvasNode.BodyUnselected>
                                <LastGiftMadeFormValuesDisplay values={getValues()} />
                            </CanvasNode.BodyUnselected>
                        </CanvasNode.Body>
                    </CanvasNode.Container>
                </CanvasNode.Root>
            </StepFormContainer>
        </Form.Provider>
    );
};

const transformSelectedInterval = (value: TimeAgoIntervalValue) => {
    return value === 'custom' ? 'Custom' : `${value} ago`;
};

const transformSelectedUnit = (value: TimeAgoCustomUnit, customAmount: string) => {
    const count = unmaskNumber(customAmount) || 0;
    const valueMap = {
        week: `${pluralize(count, 'Week')} ago`,
        month: `${pluralize(count, 'Month')} ago`,
        quarter: `${pluralize(count, 'Quarter')} ago`,
        year: `${pluralize(count, 'Year')} ago`,
    };
    return valueMap[value];
};

interface LastGiftMadeFormValuesDisplayProps {
    values: LastGiftMadeTriggerFormValues;
    showLabel?: boolean;
}
const LastGiftMadeFormValuesDisplay = ({
    values,
    showLabel = false,
}: LastGiftMadeFormValuesDisplayProps) => {
    const { campaigns, interval, customAmount, customUnit } = values;
    const trigger = enabledTriggers.find(
        t => t.slug === WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION,
    );
    if (!trigger) return null;

    return (
        <div className="flex flex-col gap-2">
            {showLabel ? <p className="text-sm font-medium text-gray-900">{trigger.name}</p> : null}
            <NodeBadges.Root>
                <CampaignBadges campaigns={campaigns} />
                <NodeBadges.Separator>AND</NodeBadges.Separator>
                <NodeBadges.Badge>
                    {interval === 'custom' ? (
                        <span className="text-gray-900">
                            {customAmount}{' '}
                            {lowerCase(transformSelectedUnit(customUnit, customAmount))}
                        </span>
                    ) : (
                        <span className="text-gray-900">{transformSelectedInterval(interval)}</span>
                    )}
                </NodeBadges.Badge>
            </NodeBadges.Root>
        </div>
    );
};

export { LastGiftMadeTrigger, LastGiftMadeFormValuesDisplay };
